import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

import * as logger from '@/utils/logger'

import {
  generateJSON,
  type Attribute,
  type JSONContent,
  type ReactNodeViewProps,
} from '@tiptap/react'

import {
  renderToHTMLString,
  renderToReactElement,
  type NodeProps,
} from '@tiptap/static-renderer'

import {
  EMPTY_PARAGRAPH_HTML,
  EMPTY_PARAGRAPH_JSON,
  TIPTAP_EXTENSIONS,
} from './tiptap-config'

import { IMAGENODE_NAME } from './image/ImageNode'
import { ImageNodeViewStatic } from './image/ImageNodeView'

import { FILENODE_NAME } from './file/FileNode'
import { FileNodeViewStatic } from './file/FileNodeView'

import { VariableNodeViewStatic } from './variables/VariableNodeView'
import { VARIABLENODE_NAME } from './variables/variables.config'

import { MentionNodeViewStatic } from './mentions/MentionNodeView'
import { MENTIONSNODE_NAME } from './mentions/mentions.config'

/**
 * Helper type for NodeView's that need to be rendered in the editor.
 */
export type NodeViewLiveProps<T extends HTMLElement = HTMLDivElement> =
  ReactNodeViewProps<T>

/**
 * Helper type for NodeView's that need to be statically rendered.
 * They must be used in `nodeMapping`, which provides different types
 * than the live `ReactNodeViewRenderer` function.
 */
export type NodeViewStaticProps = NodeProps<ProseMirrorNode>

/**
 * Helper type for the node component that will be rendered either
 * statically or in a live editor.
 */
export type NodeComponentProps = {
  node: { attrs: ProseMirrorNode['attrs'] }
}

export type NodeAttributes<T> = Record<keyof T, Attribute>

/**
 * Generates Tiptap JSON doc from HTML string.
 *
 * @note Safe: If content is undefined or empty, returns doc with empty paragraph.
 */
export function createJSONContentFromHTMLContent(contentAsHTML: string) {
  return generateJSON(
    contentAsHTML || EMPTY_PARAGRAPH_HTML, // Safe fallback
    TIPTAP_EXTENSIONS,
  )
}

export function renderJSONContentAsHTMLString(contentAsJSON: JSONContent) {
  return renderToHTMLString({
    content: contentAsJSON || EMPTY_PARAGRAPH_JSON, // Safe fallback
    extensions: TIPTAP_EXTENSIONS,
  })
}

export function renderJSONContentAsReactNode(contentAsJSON: JSONContent) {
  return renderToReactElement({
    extensions: TIPTAP_EXTENSIONS,
    content: contentAsJSON,
    options: {
      /**
       * This mapping is required for custom nodes that use `ReactNodeViewRenderer`.
       * If the node is listed in `extensions`, but not mapped it wil fall back to `renderHTML()`.
       *
       * See: https://tiptap.dev/docs/editor/api/utilities/static-renderer#react-nodeviews
       */
      nodeMapping: {
        [IMAGENODE_NAME]: ImageNodeViewStatic,
        [FILENODE_NAME]: FileNodeViewStatic,
        [VARIABLENODE_NAME]: VariableNodeViewStatic,
        [MENTIONSNODE_NAME]: MentionNodeViewStatic,
      },
      // Warn about unknown marks and nodes
      unhandledMark: (ctx) => {
        logger.warn('[Preview] unhandledMark', ctx)
      },
      unhandledNode: (ctx) => {
        logger.warn('[Preview] unhandledNode', ctx)
      },
    },
  })
}
